import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { LuArrowDown, LuArrowUp, LuClock, LuCrown, LuPlay, LuSearch, LuStar } from "react-icons/lu";
import Seo from "../components/Seo";
import DownloadButton from "../components/DownloadButton";
import NotFound from "./NotFound";
import { useJackpot, useNow, useStarline } from "../hooks/useData";
import { APK_URL, BRAND, siteUrl } from "../utils/config";
import { buildWeeks, DAY_LABELS, slotStatus } from "../utils/data";
import { trackDownload } from "../utils/analytics";

// Everything that differs between the two hourly-slot products lives here.
const KINDS = {
  starline: {
    use: useStarline,
    label: "King Starline",
    short: "Starline",
    icon: LuStar,
    path: "/starline",
    placeholder: "XXX - X",
    text: (d) => (d ? `${d.panna} - ${d.digit}` : null),
    search: (d) => `${d.panna} ${d.digit}`,
    intro:
      "King Starline declares one pana and its digit every hour, all day. Open any slot to see its full week-by-week chart.",
    about: [
      "King Starline is an hourly matka game. Unlike the main markets that declare an open and a close, every Starline slot draws a single three-digit pana, and the digit is the last digit of its sum.",
      "Each slot — from 10:30 AM to 09:30 PM — has its own chart, so you can follow one time of day over many weeks. Rows are weeks, columns are Monday to Sunday and the newest week is on top.",
    ],
    faq: [
      ["What is King Starline?", "King Starline is an hourly matka game with a new result at every slot, from 10:30 AM to 09:30 PM."],
      ["How is the Starline digit made?", "Add the three digits of the pana and keep the last digit. For example, pana 579 adds up to 21, so the digit is 1."],
      ["When is the Starline result declared?", "A result is declared for every hourly slot. Open the slot's chart to see the earlier results."],
    ],
  },
  jackpot: {
    use: useJackpot,
    label: "King Jackpot",
    short: "Jackpot",
    icon: LuCrown,
    path: "/jackpot",
    placeholder: "XX",
    text: (d) => (d ? d.value : null),
    search: (d) => d.value,
    intro: "King Jackpot declares one result every hour, all day. Open any slot to see its full week-by-week chart.",
    about: [
      "King Jackpot is an hourly game with one jodi-style result at every slot. Every slot has its own record so you can study each time of day separately.",
      "Rows are weeks, columns are Monday to Sunday and the newest week is on top. Use the search box to find any number in the whole archive.",
    ],
    faq: [
      ["What is King Jackpot?", "King Jackpot is an hourly matka game with a new result at every slot through the day."],
      ["How do I read the Jackpot chart?", "Each row is a week from Monday to Sunday. Every cell is that day's result for the slot."],
      ["Are all Jackpot slots shown?", "Yes. Every slot has its own chart; results appear as soon as they are declared."],
    ],
  },
};

const faqSchema = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map(([q, a]) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

const crumbs = (origin, items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${origin}${path}` })),
});

const latest = (days) => {
  const keys = Object.keys(days || {}).sort();
  return keys.length ? { date: keys[keys.length - 1], ...days[keys[keys.length - 1]] } : null;
};

export const SlotsOverview = ({ kind }) => {
  const K = KINDS[kind];
  const { slots, bySlug, live } = K.use();
  const now = useNow();
  const today = now.format("YYYY-MM-DD");
  const origin = siteUrl();
  const Icon = K.icon;

  const cards = useMemo(
    () =>
      slots.map((s) => {
        const days = bySlug[s.slug] || {};
        return { ...s, today: days[today] || null, last: latest(days), status: slotStatus(s.name, now) };
      }),
    [slots, bySlug, today, now]
  );

  return (
    <main className="rt-main">
      <Seo
        title={`${K.label} Result Today – ${K.short} Chart & Live Results | ${BRAND.name}`}
        description={`${K.label} result today with every hourly slot (${slots[0]?.name} to ${slots[slots.length - 1]?.name}). Open the ${K.short} chart of any slot on ${BRAND.name}.`}
        path={K.path}
        schema={[crumbs(origin, [["Home", "/"], [`${K.label}`, K.path]]), faqSchema(K.faq)]}
      />

      <section className="rt-phead">
        <div className="rt-wrap">
          <nav className="rt-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <b>{K.label}</b>
          </nav>
          <span className="rt-eyebrow rt-eyebrow--dark">
            <Icon size={13} aria-hidden="true" /> Hourly games
          </span>
          <h1>{K.label} Result Today &amp; Charts</h1>
          <p>{K.intro}</p>
          <div className="rt-tabs rt-tabs--lg">
            <Link to="/starline" className={kind === "starline" ? "is-on" : ""}>
              Starline
            </Link>
            <Link to="/jackpot" className={kind === "jackpot" ? "is-on" : ""}>
              Jackpot
            </Link>
          </div>
        </div>
      </section>

      <section className="rt-wrap rt-section">
        {!live && (
          <p className="rt-notice">
            Live {K.short} results will appear here as soon as they are published. The slots and their charts are ready.
          </p>
        )}
        <div className="rt-grid">
          {cards.map((c) => (
            <article key={c.slug} className="rt-game">
              <header>
                <h2 className="rt-game__h">
                  <Icon size={15} aria-hidden="true" /> {c.name}
                </h2>
                <span className={`rt-badge ${c.status.open ? "rt-badge--open" : "rt-badge--closed"}`}>{c.status.text}</span>
              </header>
              <div className={`rt-game__result${c.today ? " is-live" : ""}`}>{K.text(c.today) || K.placeholder}</div>
              {c.last && (
                <p className="rt-game__last">
                  Last: <b>{K.text(c.last)}</b> · {moment(c.last.date).format("DD MMM")}
                </p>
              )}
              <div className="rt-game__btns rt-game__btns--two">
                <a href={APK_URL} download rel="nofollow noopener" onClick={trackDownload} className="rt-play">
                  <LuPlay size={14} aria-hidden="true" /> Play
                </a>
                <Link to={`${K.path}/${c.slug}`} className="rt-mini" title={`${c.name} ${K.short} chart`}>
                  Chart
                </Link>
              </div>
            </article>
          ))}
        </div>
        <div className="rt-cta-row">
          <DownloadButton />
        </div>
      </section>

      <section className="rt-section rt-section--tint">
        <div className="rt-wrap rt-prose">
          <h2>About {K.label}</h2>
          {K.about.map((t) => (
            <p key={t}>{t}</p>
          ))}
          <h3>Frequently asked questions</h3>
          {K.faq.map(([q, a]) => (
            <div key={q}>
              <h4>{q}</h4>
              <p>{a}</p>
            </div>
          ))}
          <p className="rt-prose__links">
            <Link to="/matka-result-today">Matka result today</Link>
            <Link to="/charts">All matka charts</Link>
            <Link to={kind === "starline" ? "/jackpot" : "/starline"}>
              {kind === "starline" ? "King Jackpot" : "King Starline"}
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
};

export const SlotChart = ({ kind }) => {
  const K = KINDS[kind];
  const { slug } = useParams();
  const { slots, bySlug, live, loading } = K.use();
  const [query, setQuery] = useState("");
  const origin = siteUrl();

  const slot = slots.find((s) => s.slug === slug);
  const weeks = useMemo(() => buildWeeks(bySlug[slug]), [bySlug, slug]);
  const q = query.trim().toLowerCase();

  if (!slot) return loading ? <main className="rt-main"><p className="rt-empty">Loading chart…</p></main> : <NotFound />;

  const path = `${K.path}/${slug}`;
  const title = `${slot.name} ${K.short} Chart – ${K.label} Result Record | ${BRAND.name}`;
  const faq = [
    [`What is the ${slot.name} ${K.short} chart?`, `It is the week-by-week record of the ${K.label} ${slot.name} slot, from Monday to Sunday.`],
    [`When does ${slot.name} ${K.short} declare?`, `The ${slot.name} slot declares once a day at its time; results are added to this chart as soon as they are published.`],
  ];
  const hit = (d) => !!q && !!d && K.search(d).toLowerCase().includes(q);

  return (
    <main className="rt-main">
      <Seo
        title={title}
        description={`${slot.name} ${K.label} chart: the daily result record of the ${slot.name} slot, Monday to Sunday, on ${BRAND.name}. Search any number in the archive.`}
        path={path}
        schema={[crumbs(origin, [["Home", "/"], [K.label, K.path], [`${slot.name} ${K.short} Chart`, path]]), faqSchema(faq)]}
      />

      <section className="rt-phead">
        <div className="rt-wrap">
          <nav className="rt-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to={K.path}>{K.label}</Link>
            <span>/</span>
            <b>{slot.name}</b>
          </nav>
          <span className="rt-eyebrow rt-eyebrow--dark">{K.short} Chart</span>
          <h1>
            {slot.name} {K.short} Chart
          </h1>
          <p>
            {K.label} results of the {slot.name} slot, updated live.{" "}
            <span className="rt-phead__time">
              <LuClock size={14} aria-hidden="true" /> {slot.name}
            </span>
          </p>
        </div>
      </section>

      <section className="rt-wrap rt-chart">
        <div className="rt-chart__bar">
          <h2>
            {slot.name} {K.short.toUpperCase()} RECORD
          </h2>
          <label className="rt-search">
            <span className="rt-sr">Find a number in the chart</span>
            <LuSearch size={16} aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find number" inputMode="numeric" />
          </label>
        </div>

        {!live && (
          <p className="rt-notice">Results for this slot will appear here as soon as they are published.</p>
        )}

        <div className="rt-tablewrap">
          <table className="rt-table">
            <caption className="rt-sr">
              {slot.name} {K.short} chart, week by week
            </caption>
            <thead>
              <tr>
                <th scope="col">Date range</th>
                {DAY_LABELS.map((d) => (
                  <th key={d} scope="col">
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.length === 0 && (
                <tr>
                  <td colSpan={8} className="rt-table__empty">
                    No records available yet.
                  </td>
                </tr>
              )}
              {weeks.map((w) => (
                <tr key={w.key}>
                  <th scope="row" className="rt-table__date">
                    <span>{w.from}</span>
                    <i>-</i>
                    <span>{w.to}</span>
                  </th>
                  {w.cells.map((d, i) => (
                    <td key={i} className={hit(d) ? "is-hit" : ""}>
                      {!d ? null : kind === "starline" ? (
                        <div className="rt-pana">
                          <span className="rt-pana__stack">
                            {d.panna.split("").map((c, j) => (
                              <b key={j}>{c}</b>
                            ))}
                          </span>
                          <span className="rt-pana__jodi">{d.digit}</span>
                        </div>
                      ) : (
                        <span className="rt-jodi">{d.value}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="rt-howto">
          <strong>How to read:</strong> every row is one week (Monday to Sunday).{" "}
          {kind === "starline"
            ? "Each cell shows the pana (top to bottom) and, beside it, its digit."
            : "Each cell is that day's result for this slot."}
        </p>

        <div className="rt-chart__actions">
          <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <LuArrowUp size={16} aria-hidden="true" /> Go to top
          </button>
          <button type="button" onClick={() => document.getElementById("related")?.scrollIntoView({ behavior: "smooth" })}>
            <LuArrowDown size={16} aria-hidden="true" /> Other slots
          </button>
        </div>
      </section>

      <section className="rt-wrap rt-cta-row">
        <DownloadButton />
      </section>

      <section className="rt-section" id="related">
        <div className="rt-wrap">
          <h2 className="rt-title rt-title--sm">More {K.label} Slots</h2>
          <div className="rt-pills">
            {slots
              .filter((s) => s.slug !== slug)
              .map((s) => (
                <Link key={s.slug} to={`${K.path}/${s.slug}`}>
                  {s.name}
                </Link>
              ))}
            <Link to={K.path} className="is-accent">
              All {K.short} slots
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};
